import { FilterMethod, WhitespaceType } from "@/generated/company-search";
import type { CompanyFilters } from "../../types";
import {
  defaultUploadedAccountsSlice,
  type UploadedAccountsSlice,
} from "./slice";

/**
 * Converts slice state to CompanyFilters for API queries.
 * Only produces filter when in SUCCESS state with valid fileId.
 */
export function toCompanyFilters(
  slice: UploadedAccountsSlice
): Partial<CompanyFilters> {
  if (slice.uploadState !== "SUCCESS" || !slice.fileId) {
    return {};
  }

  return {
    companyMatch: {
      fileId: slice.fileId,
      filterMethod: slice.filterMethod,
      fileName: slice.fileName,
      matchedRows: slice.matchedRows,
      invalidRows: slice.invalidRows,
      totalRows: slice.totalRows,
      includeHierarchy: slice.includeHierarchy,
      uploaded: slice.uploadedAt,
      lastMatched: slice.matchedAt,
    },
    ...(slice.filterMethod === FilterMethod.EXCLUDE_MATCHED && {
      whitespace: { type: WhitespaceType.MATCH_FILE },
    }),
    ...(slice.isPlatformPermissible !== null ||
    slice.isV1Dataset !== null ||
    slice.rgifExclusiveCompany !== null
      ? {
          companyMatchUi: {
            isPlatformPermissible: slice.isPlatformPermissible,
            isV1Dataset: slice.isV1Dataset,
            rgifExclusiveCompany: slice.rgifExclusiveCompany,
          },
        }
      : {}),
  };
}

/**
 * Hydrates slice state from saved CompanyFilters.
 * Assumes SUCCESS state for persisted data.
 */
export function fromCompanyFilters(
  filters: CompanyFilters
): UploadedAccountsSlice {
  if (!filters.companyMatch?.fileId) {
    return defaultUploadedAccountsSlice;
  }

  return {
    uploadState: "SUCCESS",
    fileId: filters.companyMatch.fileId,
    fileName: filters.companyMatch.fileName ?? null,
    matchedRows: filters.companyMatch.matchedRows ?? null,
    invalidRows: filters.companyMatch.invalidRows ?? null,
    totalRows: filters.companyMatch.totalRows ?? null,
    includeHierarchy: filters.companyMatch.includeHierarchy ?? false,
    enforceCountryMatch: filters.companyMatch.enforceCountryMatch ?? false,
    filterMethod: filters.companyMatch.filterMethod ?? FilterMethod.FILTER,
    error: null,
    uploadedAt: filters.companyMatch.uploaded ?? null,
    matchedAt: filters.companyMatch.lastMatched ?? null,
    isPlatformPermissible:
      filters.companyMatchUi?.isPlatformPermissible ?? null,
    isV1Dataset: filters.companyMatchUi?.isV1Dataset ?? null,
    rgifExclusiveCompany: filters.companyMatchUi?.rgifExclusiveCompany ?? null,
  };
}
